import APIManager from "../utilities/apimanager";
import AuthenticationAuthorizationError from "../utilities/errors/authenticationAuthorizationError";
import {
  EErrorOrigin,
  EConfirmDownloadErrorCode,
  ERequestDownloadErrorCode,
} from "../utilities/enums";

import { isAuthenticationAuthorizationError } from "../utilities/errors/authenticationAuthorizationError";
import {
  clearReaderSession,
  setReaderSession,
} from "../services/readerSession";

/**
 * @typedef {Object} AuthorizeDownloadParams
 * @property {number} sessionId - The session ID of the user
 * @property {number} userId - The user ID of the user
 * @property {number} subscriptionId - The subscription ID of the user
 * @property {string} contentPackageId - The content package ID of the user
 * @property {string} paymentMethod - The payment method of the user
 */

/**
 * @typedef {Object} AuthorizeDownloadResult
 * @property {number} orderId - The order ID of the authorized download
 * @property {number} downloadId - The download ID of the authorized download
 * @property {string} downloadStatus - The status of the authorized download
 */

/**
 * Checks whether the user is authorized for a certain publication download, with optional confirmation.
 *
 * @param {AuthorizeDownloadParams} authorizeDownloadParams - The parameters for authorizing the download
 * @param {boolean} confirmDownload - Whether to confirm the download after authorization
 * @returns {Promise<AuthorizeDownloadResult>} - Promise object containing the authorized download's order and download ID
 * @throws {AuthenticationAuthorizationError} - If the download authorization or confirmation fails
 */
export async function checkAuthorizationForPublication(
  authorizeDownloadParams,
  confirmDownload
) {
  if (confirmDownload) {
    return authorizeAndConfirmDownload(authorizeDownloadParams);
  } else {
    return authorizeDownloadWithoutConfirming(authorizeDownloadParams);
  }
}

/**
 * Checks whether the user is authorized for a certain download.
 * If successful, also confirms that download using the /ConfirmDownload API endpoint.
 *
 * @param {AuthorizeDownloadParams} params - The parameters for authorizing the download
 * @returns {Promise<AuthorizeDownloadResult>} - Promise object containing the authorized download's order and download ID
 * @throws {AuthenticationAuthorizationError} - If the download authorization or confirmation fails
 */
async function authorizeAndConfirmDownload(params) {
  const authorizedDownloadData = await authorizeDownloadAndUpdateReaderSession(
    params
  );

  try {
    await APIManager.confirmDownload({
      userId: params.userId,
      sessionId: params.sessionId,
      contentPackageId: params.contentPackageId,
      downloadId: authorizedDownloadData.downloadId,
      downloadStatus: authorizedDownloadData.downloadStatus,
    });
    return authorizedDownloadData;
  } catch (error) {
    throw new AuthenticationAuthorizationError(
      error,
      EErrorOrigin.CONFIRMDOWNLOAD,
      EConfirmDownloadErrorCode.CALL_FAILED
    );
  }
}

/**
 * Checks whether the user is authorized for a certain download.
 *
 * @param {AuthorizeDownloadParams} params - The parameters for authorizing the download
 * @returns {Promise<AuthorizeDownloadResult>} - Promise object containing the authorized download's order and download ID
 * @throws {AuthenticationAuthorizationError} - If the download authorization fails
 */
async function authorizeDownloadWithoutConfirming(params) {
  return authorizeDownloadAndUpdateReaderSession(params);
}

/**
 * Checks whether the user is authorized for a certain download, using the /RequestDownload API endpoint.
 * Before doing so, the reader session is cleared.
 * After success, the reader session is updated accordingly to allow further authorization checks for the same download to bypass the /RequestDownload API call.
 *
 * @param {AuthorizeDownloadParams} params - The parameters for authorizing the download
 * @returns {Promise<{orderId: string, downloadId: string}>} Promise object containing the authorized download's order and download ID
 * @throws {AuthenticationAuthorizationError} If the download authorization fails
 */
async function authorizeDownloadAndUpdateReaderSession({
  sessionId,
  userId,
  subscriptionId,
  contentPackageId,
  paymentMethod,
}) {
  try {
    clearReaderSession();
    const requestDownloadResponse = await APIManager.requestDownload(
      userId,
      sessionId,
      subscriptionId,
      contentPackageId,
      paymentMethod
    );

    if (!isSuccessfulRequestDownloadResponse(requestDownloadResponse)) {
      throw new AuthenticationAuthorizationError(
        requestDownloadResponse,
        EErrorOrigin.REQUESTDOWNLOAD
      );
    }

    setReaderSession({
      userId: userId,
      sessionId: sessionId,
      subscriptionId: subscriptionId,
      orderId: requestDownloadResponse.OrderID,
      downloadId: requestDownloadResponse.DownloadID,
      cpId: contentPackageId,
    });

    return {
      orderId: requestDownloadResponse.OrderID,
      downloadId: requestDownloadResponse.DownloadID,
      downloadStatus: requestDownloadResponse.DownloadStatus,
    };
  } catch (error) {
    handleRequestDownloadError(error);
  }
}

function isSuccessfulRequestDownloadResponse(response) {
  return (
    response &&
    response.StatusMessage &&
    response.StatusMessage.toLowerCase() === "ok" &&
    response.DownloadStatus &&
    response.DownloadStatus.toLowerCase() !== "refused"
  );
}

function handleRequestDownloadError(error) {
  if (error && isAuthenticationAuthorizationError(error)) {
    throw error;
  }

  throw new AuthenticationAuthorizationError(
    error,
    EErrorOrigin.REQUESTDOWNLOAD,
    ERequestDownloadErrorCode.CALL_FAILED
  );
}

export function getPaymentMethod(subscriptionId, paymentMethods) {
  if (subscriptionId) {
    const subscriptionIdInt = parseInt(subscriptionId);
    const paymentMethod =
      subscriptionIdInt && subscriptionIdInt > 0
        ? paymentMethods.SUBSCRIPTION
        : paymentMethods.UNKNOWN;

    return paymentMethod;
  }
  return paymentMethods.UNKNOWN;
}



// WEBPACK FOOTER //
// ./src/common/subscriptionmanagement/downloadAuthorization.js